import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Toaster, toast } from "sonner";
import {
  LogOut,
  RefreshCw,
  Search,
  Users,
  MailCheck,
  Mail,
  MailX,
  Activity,
  Calendar,
  Download,
  CircleDot,
  Terminal,
  ChevronDown,
  Phone,
  Briefcase,
  MessageSquare,
} from "lucide-react";
import {
  adminClient,
  clearAdminToken,
  formatApiError,
} from "@/lib/adminApi";
import { ADMIN } from "@/constants/testIds";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_prompt-mastery-64/artifacts/3omzwe9h_1773800644378.png";

const formatDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const relative = (iso) => {
  try {
    const diff = Date.now() - new Date(iso).getTime();
    const m = Math.floor(diff / 60000);
    if (m < 1) return "just now";
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    return `${d}d ago`;
  } catch {
    return "";
  }
};

const DetailField = ({ icon: Icon, label, value, multiline }) => {
  if (!value) return null;
  return (
    <div className="border border-white/10 bg-slate-900/50 p-4">
      <div className="flex items-center gap-2 mb-2 font-mono text-[10px] uppercase tracking-[0.25em] text-cyan-400">
        <Icon className="h-3 w-3" />
        {label}
      </div>
      <div
        className={`font-mono text-xs text-slate-200 ${
          multiline ? "whitespace-pre-wrap leading-relaxed" : "truncate"
        }`}
      >
        {value}
      </div>
    </div>
  );
};


const StatCard = ({ label, value, icon: Icon, testId, hint }) => (
  <div className="relative border border-white/10 bg-slate-900/40 backdrop-blur-sm p-5 sm:p-6 hover:border-cyan-500/40 transition-colors group">
    <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-500/0 to-transparent group-hover:via-cyan-500/60 transition-all duration-500" />
    <div className="flex items-center justify-between mb-3">
      <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400">
        {label}
      </span>
      <div className="h-8 w-8 flex items-center justify-center border border-cyan-500/30 bg-cyan-500/10 text-cyan-400">
        <Icon className="h-4 w-4" />
      </div>
    </div>
    <div
      data-testid={testId}
      className="font-display text-3xl sm:text-4xl font-black text-white tabular-nums"
    >
      {value}
    </div>
    {hint && (
      <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
        {hint}
      </div>
    )}
  </div>
);

export default function AdminLeadsPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [query, setQuery] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const [leadsRes, statsRes] = await Promise.all([
        adminClient.get("/admin/leads"),
        adminClient.get("/admin/leads/stats"),
      ]);
      setLeads(leadsRes.data || []);
      setStats(statsRes.data || null);
    } catch (err) {
      if (err?.response?.status !== 401) {
        toast.error("Failed to load leads", {
          description: formatApiError(err?.response?.data?.detail),
        });
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const logout = () => {
    clearAdminToken();
    toast.success("Signed out.");
    navigate("/admin/login", { replace: true });
  };

  const filtered = useMemo(() => {
    if (!query.trim()) return leads;
    const q = query.trim().toLowerCase();
    return leads.filter(
      (l) =>
        l.email?.toLowerCase().includes(q) ||
        l.name?.toLowerCase().includes(q) ||
        l.source?.toLowerCase().includes(q)
    );
  }, [leads, query]);

  const exportCsv = () => {
    if (!filtered.length) {
      toast.message("Nothing to export.");
      return;
    }
    const header = ["id", "name", "email", "source", "business", "phone", "message", "email_sent", "email_id", "created_at"];
    const rows = filtered.map((l) =>
      header
        .map((k) => {
          const v = l[k] ?? "";
          const s = typeof v === "string" ? v : JSON.stringify(v);
          return `"${s.replace(/"/g, '""')}"`;
        })
        .join(",")
    );
    const csv = [header.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `jk-academy-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid={ADMIN.dashboard} className="min-h-screen bg-[#030712] text-slate-100">
      <Toaster
        theme="dark"
        position="bottom-right"
        toastOptions={{
          className: "font-mono",
          style: {
            background: "#0F172A",
            border: "1px solid rgba(6,182,212,0.4)",
            color: "#F8FAFC",
            borderRadius: 0,
          },
        }}
      />

      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-slate-950/80 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 flex items-center justify-between h-16 sm:h-20">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="JK Academy" className="h-9 w-9 sm:h-10 sm:w-10 object-contain" />
            <div className="leading-tight">
              <div className="font-display font-extrabold tracking-tight text-white text-base sm:text-lg">
                JK ACADEMY
              </div>
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-cyan-400">
                Admin · Leads dashboard
              </div>
            </div>
          </div>
          <button
            data-testid={ADMIN.logoutButton}
            onClick={logout}
            className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-slate-300 hover:text-cyan-300 border border-white/10 hover:border-cyan-500/40 px-4 py-2.5 transition-colors"
          >
            <LogOut className="h-4 w-4" />
            Sign out
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 sm:px-8 py-10 sm:py-14">
        {/* Title row */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
          <div>
            <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-cyan-400 mb-3">
              <CircleDot className="h-3 w-3 animate-pulse" />
              <span>/ leads.live_feed</span>
            </div>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white">
              Captured leads
            </h1>
            <p className="mt-2 text-slate-400 font-mono text-sm">
              Every submission across the landing page and services consultations.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid={ADMIN.refreshButton}
              onClick={load}
              disabled={refreshing}
              className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-slate-300 hover:text-cyan-300 border border-white/10 hover:border-cyan-500/40 px-4 py-2.5 disabled:opacity-50 transition-colors"
            >
              <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
              Refresh
            </button>
            <button
              data-testid="admin-export-button"
              onClick={exportCsv}
              className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-slate-950 bg-cyan-500 hover:bg-cyan-400 px-4 py-2.5 font-bold shadow-[0_0_20px_rgba(6,182,212,0.35)] transition-colors"
            >
              <Download className="h-4 w-4" />
              Export CSV
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 mb-10">
          <StatCard
            label="Total leads"
            value={stats?.total ?? "—"}
            icon={Users}
            testId={ADMIN.statTotal}
            hint="All time"
          />
          <StatCard
            label="Last 24h"
            value={stats?.last_24h ?? "—"}
            icon={Activity}
            testId={ADMIN.stat24h}
            hint="Most recent day"
          />
          <StatCard
            label="Last 7 days"
            value={stats?.last_7d ?? "—"}
            icon={Calendar}
            testId={ADMIN.stat7d}
            hint="Rolling week"
          />
          <StatCard
            label="Email delivery"
            value={stats ? `${stats.email_delivery_rate}%` : "—"}
            icon={MailCheck}
            testId={ADMIN.statRate}
            hint="Resend success rate"
          />
        </div>

        {/* Search */}
        <div className="mb-6 relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
          <input
            data-testid={ADMIN.searchInput}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name, email, or source…"
            className="w-full bg-slate-950 border border-white/10 text-white placeholder:text-slate-600 h-11 pl-10 pr-4 font-mono text-sm focus:outline-none focus:ring-1 focus:ring-cyan-500 focus:border-cyan-500 transition-colors"
          />
        </div>

        {/* Leads table */}
        <div className="relative border border-white/10 bg-slate-900/40 backdrop-blur-sm overflow-hidden">
          <div className="absolute top-0 left-0 h-3 w-3 border-t border-l border-cyan-400" />
          <div className="absolute top-0 right-0 h-3 w-3 border-t border-r border-cyan-400" />
          <div className="absolute bottom-0 left-0 h-3 w-3 border-b border-l border-cyan-400" />
          <div className="absolute bottom-0 right-0 h-3 w-3 border-b border-r border-cyan-400" />

          <div className="flex items-center gap-2 px-5 py-3 border-b border-white/10 bg-slate-950/60">
            <Terminal className="h-3.5 w-3.5 text-cyan-400" />
            <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-cyan-400">
              leads.collection
            </span>
            <span className="ml-auto font-mono text-[11px] text-slate-500">
              {filtered.length} {filtered.length === 1 ? "result" : "results"}
            </span>
          </div>

          {loading ? (
            <div className="px-6 py-16 text-center font-mono text-sm text-slate-500">
              Loading…
            </div>
          ) : filtered.length === 0 ? (
            <div
              data-testid={ADMIN.emptyState}
              className="px-6 py-16 text-center"
            >
              <div className="mx-auto h-12 w-12 flex items-center justify-center border border-white/10 bg-slate-950 text-slate-500 mb-4">
                <Users className="h-5 w-5" />
              </div>
              <p className="font-display text-lg text-white font-semibold">
                {query ? "No leads match your search." : "No leads yet."}
              </p>
              <p className="mt-1 font-mono text-xs text-slate-500">
                {query
                  ? "Try a different keyword."
                  : "When someone submits the form, you'll see them here."}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table data-testid={ADMIN.leadsTable} className="w-full text-left">
                <thead>
                  <tr className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500 border-b border-white/5">
                    <th className="px-5 py-3 font-medium w-8"></th>
                    <th className="px-5 py-3 font-medium">Name</th>
                    <th className="px-5 py-3 font-medium">Email</th>
                    <th className="px-5 py-3 font-medium">Source</th>
                    <th className="px-5 py-3 font-medium">Delivery</th>
                    <th className="px-5 py-3 font-medium">Submitted</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((l) => {
                    const hasDetails = l.business || l.phone || l.message;
                    const isOpen = expandedId === l.id;
                    return (
                      <React.Fragment key={l.id}>
                        <tr
                          data-testid={ADMIN.leadRow(l.id)}
                          className="border-b border-white/5 hover:bg-cyan-500/[0.04] transition-colors"
                        >
                          <td className="px-5 py-4 whitespace-nowrap">
                            {hasDetails ? (
                              <button
                                data-testid={`admin-lead-expand-${l.id}`}
                                onClick={() => setExpandedId(isOpen ? null : l.id)}
                                aria-expanded={isOpen}
                                aria-controls={`admin-lead-details-${l.id}`}
                                aria-label={isOpen ? "Hide details" : "Show details"}
                                className={`h-7 w-7 flex items-center justify-center border border-white/15 hover:border-cyan-400 hover:text-cyan-300 text-slate-400 transition-all ${
                                  isOpen ? "bg-cyan-500/10 text-cyan-300 border-cyan-400/60" : ""
                                }`}
                              >
                                <ChevronDown
                                  className={`h-3.5 w-3.5 transition-transform ${isOpen ? "rotate-180" : ""}`}
                                />
                              </button>
                            ) : (
                              <span className="text-slate-700 text-xs">—</span>
                            )}
                          </td>
                          <td className="px-5 py-4 font-display text-white font-semibold whitespace-nowrap">
                            {l.name}
                          </td>
                          <td className="px-5 py-4 font-mono text-sm text-slate-300 break-all">
                            {l.email}
                          </td>
                          <td className="px-5 py-4 font-mono text-[11px] uppercase tracking-[0.15em] text-slate-500 whitespace-nowrap">
                            {l.source}
                          </td>
                          <td className="px-5 py-4 whitespace-nowrap">
                            {l.email_sent ? (
                              <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-cyan-300 border border-cyan-500/40 bg-cyan-500/10 px-2 py-1">
                                <Mail className="h-3 w-3" />
                                Sent
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-amber-300 border border-amber-500/40 bg-amber-500/10 px-2 py-1">
                                <MailX className="h-3 w-3" />
                                Skipped
                              </span>
                            )}
                          </td>
                          <td className="px-5 py-4 font-mono text-xs text-slate-400 whitespace-nowrap">
                            <div className="text-slate-200">{relative(l.created_at)}</div>
                            <div className="text-slate-500 text-[10px]">{formatDate(l.created_at)}</div>
                          </td>
                        </tr>
                        {hasDetails && isOpen && (
                          <tr
                            data-testid={`admin-lead-details-${l.id}`}
                            className="border-b border-white/5 bg-slate-950/40"
                          >
                            <td></td>
                            <td colSpan={5} className="px-5 py-5">
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <DetailField icon={Briefcase} label="Business" value={l.business} />
                                <DetailField icon={Phone} label="Phone" value={l.phone} />
                                <DetailField icon={MessageSquare} label="Message" value={l.message} multiline />
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
